import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import '../styles/LoginOTP.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function LoginOTP() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const email = searchParams.get('email') || '';

  const [otpCode, setOtpCode] = useState('');
  const [emailInput, setEmailInput] = useState(email);
  const [errors, setErrors] = useState({});
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    if (email) {
      setEmailInput(email);
    }
  }, [email]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'otp') {
      // Only allow digits and limit to 6 characters
      const digitsOnly = value.replace(/\D/g, '').slice(0, 6);
      setOtpCode(digitsOnly);
    } else if (name === 'email') {
      setEmailInput(value);
    }
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!emailInput) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!otpCode) {
      newErrors.otp = 'OTP code is required';
    } else if (!/^\d{6}$/.test(otpCode)) {
      newErrors.otp = 'OTP code must be 6 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsVerifying(true);

    try {
      // Ensure OTP is a string and trimmed
      const otpToSend = String(otpCode).trim();
      console.log('Sending OTP verification request:', { email: emailInput, otp_code: otpToSend });
      
      const response = await axios.post(
        `${API_BASE_URL}/auth/verify-login-otp`,
        {
          email: emailInput.trim(),
          otp_code: otpToSend,
        },
        {
          withCredentials: true,
        }
      );

      if (response.data.success) {
        // Refresh auth state
        await checkAuth();
        // Redirect to dashboard
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      if (error.response?.data?.requiresVerification) {
        // Email not verified, redirect to verification page
        navigate(`/verify-email?email=${encodeURIComponent(emailInput)}`, { replace: true });
      } else {
        setErrors({
          submit: error.response?.data?.error || 'OTP verification failed. Please try again.',
        });
      }
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="login-otp-page">
      <div className="login-otp-container">
        <h1>Enter Login OTP</h1>
        <p className="instructions">
          We've sent a 6-digit OTP code to your email address.
          Please enter it below to log in.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={emailInput}
              onChange={handleChange}
              placeholder="your.email@example.com"
              className={errors.email ? 'error' : ''}
              disabled={!!email}
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="otp">OTP Code</label>
            <input
              type="text"
              id="otp"
              name="otp"
              value={otpCode}
              onChange={handleChange}
              placeholder="000000"
              maxLength="6"
              className={errors.otp ? 'error' : ''}
              pattern="[0-9]{6}"
              autoComplete="off"
            />
            {errors.otp && <span className="error-message">{errors.otp}</span>}
          </div>

          {errors.submit && <div className="error-message submit-error">{errors.submit}</div>}

          <button type="submit" className="verify-button" disabled={isVerifying}>
            {isVerifying ? 'Verifying...' : 'Verify OTP & Login'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default LoginOTP;
